import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Switch,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../utils/feedback';
import { getApiErrorMessage } from '../../utils/apiError';
import type { Product, ProductVariant } from '../../types/pz';
import { createVariant, updateVariant, type VariantFormValues } from '../../modules/products/api';
import {
  palletPresetFieldValues,
  toLogisticsFormValues,
  type PalletPresetId,
  type ProductLogisticsInput,
} from '../../modules/products/productLogistics';
import PieceSection from '../../modules/products/card/PieceSection';
import PalletSection from '../../modules/products/card/PalletSection';
// The card sections are styled by pages/ProductDetail.css, the only page that
// opens this editor.
import './ProductVariantModal.css';

const { Text, Title } = Typography;

type AttributeRow = { name?: string; value?: string };
type VariantEditorValues = Omit<VariantFormValues, 'attributes'> & { attributes: AttributeRow[] };

interface ProductVariantModalProps {
  open: boolean;
  product: Product;
  variant?: ProductVariant | null;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

const attributeRows = (variant?: ProductVariant | null): AttributeRow[] => {
  const rows = Object.entries(variant?.attributes || {}).map(([name, value]) => ({
    name,
    value: String(value),
  }));
  return rows.length > 0 ? rows : [{ name: '', value: '' }];
};

export const getProductVariantSaveErrorKey = (code?: string) => {
  if (code === 'PRODUCT_VARIANT_HAS_STOCK') return 'products:variantEditor.stockDeactivateBlocked';
  if (code === 'PRODUCT_VARIANT_DUPLICATE_ATTRIBUTES') return 'products:variantEditor.duplicate';
  return undefined;
};

const ProductVariantModal: React.FC<ProductVariantModalProps> = ({
  open,
  product,
  variant,
  onCancel,
  onSaved,
}) => {
  const { t } = useTranslation(['products', 'common']);
  const feedback = useFeedback();
  const [form] = Form.useForm<VariantEditorValues>();
  const [saving, setSaving] = useState(false);
  const [preset, setPreset] = useState<PalletPresetId | null>(null);
  const watchedRows = Form.useWatch('attributes', form);
  const values = (Form.useWatch((all) => all, { form, preserve: true }) || {}) as ProductLogisticsInput;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setPreset(null);
    form.setFieldsValue({
      attributes: attributeRows(variant),
      ean: variant?.eanOverride ?? undefined,
      isActive: variant?.isActive !== false,
      // Owner decision 2026-09-24: a variant has its own dimensions, weight,
      // paczka and pallet. A new variant starts as a copy of the product (the
      // fast path for "same as the parent"); every field stays editable.
      ...toLogisticsFormValues(variant ? (variant.logistics ?? product) : product),
    });
  }, [form, open, variant, product]);

  const handlePresetChange = (presetId: PalletPresetId) => {
    const presetValues = palletPresetFieldValues(presetId, values);
    if (!presetValues) return;
    form.setFieldsValue(presetValues);
    setPreset(presetId);
  };

  const generatedName = useMemo(() => [...(watchedRows || [])]
    .filter((row) => row?.name?.trim() && row?.value?.trim())
    .sort((left, right) => left.name!.trim().localeCompare(right.name!.trim(), 'pl'))
    .map((row) => row.value!.trim())
    .filter(Boolean)
    .join(' · '), [watchedRows]);

  const validateAttributeRows = (_: unknown, value?: AttributeRow[]) => {
    const complete = (value || []).filter((row) => row?.name?.trim() && row?.value?.trim());
    if (complete.length === 0 || complete.length !== (value || []).length) {
      return Promise.reject(new Error(t('products:variantEditor.attributeRequired')));
    }
    const names = complete.map((row) => row.name!.trim().toLocaleLowerCase('pl'));
    if (new Set(names).size !== names.length) {
      return Promise.reject(new Error(t('products:variantEditor.duplicateAttribute')));
    }
    return Promise.resolve();
  };

  const handleSave = async () => {
    let values: VariantEditorValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const attributes = Object.fromEntries(values.attributes.map((row) => [
      row.name!.trim(),
      row.value!.trim(),
    ]));
    const payload: VariantFormValues = { ...values, attributes };
    // Prefer the attribute-derived variant name (e.g. "Antracyt · 600x2500");
    // real variants always have complete attribute rows by this point (see
    // validateAttributeRows), so this only falls back to the product name for
    // the theoretical case of an empty row set.
    const variantName = Object.values(attributes).join(' · ') || product.name;

    setSaving(true);
    try {
      if (variant) {
        await updateVariant(product.id, variant.id, payload);
        feedback.success(t('products:success.variantUpdated', { name: variantName }));
      } else {
        await createVariant(product.id, payload);
        feedback.success(t('products:success.variantCreated', { name: variantName }));
      }
      await onSaved();
      form.resetFields();
    } catch (error: any) {
      const code = error?.response?.data?.code;
      const errorKey = getProductVariantSaveErrorKey(code);
      if (errorKey) {
        feedback.error(t(errorKey), { sticky: code === 'PRODUCT_VARIANT_HAS_STOCK' });
      } else {
        feedback.error(getApiErrorMessage(error, t, 'products:error.saveVariantFailed', undefined, { name: variantName }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      width={1040}
      open={open}
      title={variant ? t('products:variantEditor.editTitle') : t('products:variantEditor.createTitle')}
      okText={t('common:save')}
      cancelText={t('common:cancel')}
      confirmLoading={saving}
      onOk={handleSave}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Form
        form={form}
        // A named form prefixes field ids: the card behind the modal has the
        // same field names, and duplicate ids would detach the labels.
        name="productVariant"
        layout="vertical"
        className="product-variant-modal"
        requiredMark={false}
        onValuesChange={(changed) => {
          if ('palletLength' in changed || 'palletWidth' in changed) setPreset(null);
        }}
      >
        <section className="product-variant-modal__section product-variant-modal__inherited">
          <Title level={5}>{t('products:variantEditor.inheritedTitle')}</Title>
          <Text type="secondary">{t('products:variantEditor.inheritedHint')}</Text>
          <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label={t('products:fields.name')}>{product.name}</Descriptions.Item>
            <Descriptions.Item label={t('products:fields.category')}>
              {product.productCategoryName || t('products:productCategory.none')}
            </Descriptions.Item>
          </Descriptions>
        </section>

        <section className="product-variant-modal__section">
          <Title level={5}>{t('products:variantEditor.attributesTitle')}</Title>
          <Text type="secondary">{t('products:variantEditor.attributesHint')}</Text>
          <Form.List name="attributes" rules={[{ validator: validateAttributeRows }]}>
              {(fields, { add, remove }, { errors }) => (
                <Space direction="vertical" className="product-variant-modal__attributes" size={8}>
                  {fields.map((field) => (
                    <Space.Compact key={field.key} block>
                      <Form.Item name={[field.name, 'name']} noStyle>
                        <Input
                          maxLength={80}
                          aria-label={t('products:variantEditor.attributeName')}
                          placeholder={t('products:variantEditor.attributeNamePlaceholder')}
                        />
                      </Form.Item>
                      <Form.Item name={[field.name, 'value']} noStyle>
                        <Input
                          maxLength={160}
                          aria-label={t('products:variantEditor.attributeValue')}
                          placeholder={t('products:variantEditor.attributeValuePlaceholder')}
                        />
                      </Form.Item>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={t('common:delete')}
                        onClick={() => remove(field.name)}
                      />
                    </Space.Compact>
                  ))}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ name: '', value: '' })}>
                    {t('products:variantEditor.addAttribute')}
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Space>
              )}
          </Form.List>
          {generatedName && (
            <Alert type="info" showIcon message={t('products:variantEditor.generatedName', { name: generatedName })} />
          )}
        </section>

        <section className="product-variant-modal__section product-variant-modal__logistics">
          <Title level={5}>{t('products:variantEditor.logisticsTitle')}</Title>
          <Text type="secondary">
            {variant ? t('products:variantEditor.logisticsHintEdit') : t('products:variantEditor.logisticsHintCreate')}
          </Text>
        </section>
        <PieceSection values={values} showHeight={values.height !== undefined} disabled={false} />
        <PalletSection values={values} disabled={false} preset={preset} onPresetChange={handlePresetChange} />

        <section className="product-variant-modal__section">
          <Title level={5}>{t('products:variantEditor.overridesTitle')}</Title>
          <Text type="secondary">{t('products:variantEditor.overridesHint')}</Text>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label={t('products:fields.ean')} name="ean">
                <Input maxLength={64} placeholder={product.ean || undefined} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('products:variantFields.isActive')} name="isActive" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </section>
      </Form>
    </Modal>
  );
};

export default ProductVariantModal;
